/**
 * Runbook Gaps — where the documented runbooks fail real engineers.
 *
 * Reads store.overridden_nodes() via GET /api/learning/overrides: the nodes
 * where engineers jumped off the engine's suggested path, most frequent
 * first. Admin-only, same gate as the other insight routes.
 *
 * Route: /learning/insights
 *
 * WHO READS THIS AND WHAT THEY NEED
 *     The reader is an admin or a runbook author, not a developer. A row must
 *     make them think "ah, THAT step is wrong, and here is what it should say
 *     instead" — so a row leads with the node's HUMAN title and its runbook,
 *     then what the engine was proposing there, then the engineer's own words,
 *     then where they went, then a link to the trail it happened in. The raw
 *     mermaid nid is demoted to a small monospace label: it is provenance for
 *     whoever edits the flowchart, not the headline.
 *
 *     Everything on the page comes from captured trails. There is no retrieval
 *     anywhere in it, so it pays off from the very first reviewed session.
 *
 * DOM CONTRACT — DO NOT REFLOW THE SHELL
 *     RunbookGapsPage.jsx injects the learning section tabs into this page by
 *     querying `.flex-1.relative .w-full.min-w-0` and then the header row
 *     `.flex.items-center.justify-between.mb-6`. Those three class strings are
 *     an interface; rename them and the sub-navbar silently disappears.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, AlertTriangle, GraduationCap, Compass, Loader2,
  ChevronDown, ChevronRight, BookOpen, Quote, ExternalLink,
} from "lucide-react";
import AdminSideBar from "../AdminSideBar";
import UserProfileMenu from "../UserProfileMenu";
import { listOverrideHotspots } from "./useLearning";
import { prettySourceTitle } from "./TrailSteps";
import { TYPE_LABEL, nodeTypeFromNid, tintOf, typeStyle } from "./TrailGraph";
import OverrideFork from "./OverrideFork";
import "./styles/scrollbars.css";

/* With this few rows the whole page fits on one screen, and an admin who has to
   click every row to learn anything has been given a list of identifiers again.
   Past this many, collapsed-by-default is the readable default. */
const AUTO_EXPAND_MAX = 3;

/* Destinations drawn per fork. Two different places engineers went is a real
   signal; five is a crowd. */
const FORK_DESTINATIONS = 2;

const asText = (v) => (typeof v === "string" ? v.trim() : "");

/** "Runbook - ZIA Traffic Forwarding.pdf" -> "Runbook - ZIA Traffic Forwarding" */
const runbookLabel = (raw) => (raw ? prettySourceTitle(raw).label : "");

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

/** The node-family chip — TrailGraph's colours, so the two pages read alike. */
function TypeChip({ type }) {
  if (!type) return null;
  const st = typeStyle({ type });
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-[1px] text-[10.5px] font-semibold"
      style={{ background: tintOf(st.bg, 0.22), color: st.text }}
    >
      {TYPE_LABEL[type] || type}
    </span>
  );
}

function StatusChip({ status }) {
  const s = asText(status);
  if (!s) return null;
  const tone =
    s === "published" ? "bg-emerald-50 text-emerald-700"
      : s === "submitted" ? "bg-amber-50 text-amber-700"
        : "bg-gray-100 text-gray-500";
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-semibold ${tone}`}>
      {s}
    </span>
  );
}

const RunbookGaps = ({ onLogout }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [titlesResolved, setTitlesResolved] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listOverrideHotspots(50);
      if (data === null) {
        setDisabled(true);
        setRows([]);
      } else {
        setDisabled(false);
        const list = data.overrides || [];
        setRows(list);
        // `titles_resolved: false` means the parsed runbook graph could not be
        // reached AT ALL — which is a different story from "this one nid has no
        // title", and the reader is told which one they are looking at.
        setTitlesResolved(data.titles_resolved !== false);
        // Everything open while the list is short: see AUTO_EXPAND_MAX.
        setOpen(
          list.length > 0 && list.length <= AUTO_EXPAND_MAX
            ? new Set(list.map((_, i) => i))
            : new Set(),
        );
      }
    } catch (e) {
      setError(e.message || "Could not load runbook gaps.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback((idx) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const { maxCount, showBar } = useMemo(() => {
    const counts = rows.map((r) => Number(r.n) || 0);
    const hi = counts.reduce((m, c) => Math.max(m, c), 0);
    const lo = counts.reduce((m, c) => Math.min(m, c), hi);
    // Show the bar only when counts vary — all-equal bars encode nothing.
    return { maxCount: hi, showBar: hi > 0 && hi !== lo };
  }, [rows]);

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-y-auto overflow-x-hidden min-w-0">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        <div className="w-full min-w-0">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">Runbook Gaps</h1>
              <p className="text-gray-500 text-sm mt-1">
                Runbook steps that engineers skipped or overrode, most frequent first.
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p>{error}</p>
                <button
                  type="button"
                  onClick={load}
                  className="mt-2 inline-flex items-center gap-1.5 text-red-700 font-semibold hover:underline"
                >
                  <RefreshCw size={13} />
                  Retry
                </button>
              </div>
            </div>
          )}

          {!error && !loading && !disabled && rows.length > 0 && !titlesResolved && (
            <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-lg text-[12.5px] flex items-start gap-2">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              <span>
                The runbook flowcharts could not be read just now, so these steps
                are shown by their raw node id. Everything else on the page is
                complete — refresh in a moment for the titles.
              </span>
            </div>
          )}

          {disabled ? (
            <div className="border border-[#EBECE6] rounded-xl bg-white shadow-sm px-6 py-12 text-center">
              <GraduationCap size={26} className="mx-auto mb-3 text-gray-300" />
              <p className="text-[#253439] font-semibold">
                The learning module is switched off
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Turn it on in{" "}
                <button
                  type="button"
                  onClick={() => navigate("/admin/settings")}
                  className="text-cyan-600 hover:underline font-medium"
                >
                  Settings
                </button>{" "}
                to start capturing where engineers leave the runbook.
              </p>
            </div>
          ) : (
            <div className="border border-[#EBECE6] rounded-xl overflow-hidden bg-white shadow-sm relative">
              {/* Subtle overlay when reloading with existing rows visible */}
              {loading && rows.length > 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-xl">
                  <Loader2 size={22} className="animate-spin text-cyan-500" />
                </div>
              )}
              {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 size={22} className="animate-spin" />
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <Compass size={26} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-[#253439] font-semibold">
                    Not enough data yet
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    This fills in as engineers jump off the suggested path
                    during review. Nothing has been captured yet.
                  </p>
                </div>
              ) : (
                <div
                  className="rgi-scroll overflow-y-auto"
                  style={{ maxHeight: "calc(100vh - 220px)" }}
                >
                  {rows.map((r, idx) => (
                    <GapRow
                      key={`${r.from_nid || "?"}-${idx}`}
                      row={r}
                      expanded={open.has(idx)}
                      onToggle={() => toggle(idx)}
                      showBar={showBar}
                      maxCount={maxCount}
                      onOpenTrail={(pk) => navigate(`/learning/${pk}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && !disabled && rows.length > 0 && (
            <p className="text-gray-400 text-xs mt-3">
              {rows.length} step{rows.length === 1 ? "" : "s"} where engineers
              chose a different path than the one suggested
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── one gap ──────────────────────────────────────────────────────────── */

function GapRow({ row, expanded, onToggle, showBar, maxCount, onOpenTrail }) {
  const count = Number(row.n) || 0;
  const examples = useMemo(
    () => (row.examples || []).filter((e) => e && asText(e.query_text)),
    [row.examples],
  );
  const title = asText(row.title);
  const nid = asText(row.from_nid);
  const type = row.node_type || (nid ? nodeTypeFromNid(nid, title) : "");
  const runbook = runbookLabel(row.runbook);
  const asked = asText(row.asked);
  const suggested = useMemo(() => row.suggested_next || [], [row.suggested_next]);

  // Where engineers actually ended up, deduped: `to_nid` is NULL on every
  // captured jump today, so most of these are derived from the next step of
  // the trail — and when the jump was the LAST step there is nothing to derive,
  // which the fork says out loud instead of drawing an arrow to nowhere.
  const destinations = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const ex of examples) {
      const d = ex.destination;
      if (!d || !d.nid || seen.has(d.nid)) continue;
      seen.add(d.nid);
      out.push(d);
      if (out.length >= FORK_DESTINATIONS) break;
    }
    return out;
  }, [examples]);
  const anyUnknown = examples.some((e) => !e.destination);
  // A branch that is ALSO one of the destinations is drawn once, as the
  // destination — that box carries strictly more (how we know they went there).
  const branches = useMemo(
    () => suggested.filter((s) => !destinations.some((d) => d.nid === s.nid)),
    [suggested, destinations],
  );

  const heading = title || nid || "Unknown node";
  const preview = asText(examples[0]?.query_text);

  return (
    <div className="border-b border-[#EBECE6] last:border-b-0">
      {/* ── collapsed line: what, where, how often, and a taste of why ── */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-[#FAFAF6] transition"
      >
        <span className="mt-0.5 text-gray-400 flex-shrink-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[14px] font-semibold text-[#253439]">
              {heading}
            </span>
            <TypeChip type={type} />
          </span>

          <span className="mt-1 flex items-center gap-2 flex-wrap text-[11.5px] text-gray-500">
            {runbook ? (
              <span className="inline-flex items-center gap-1 min-w-0">
                <BookOpen size={12} className="flex-shrink-0 text-gray-400" />
                <span className="truncate">{runbook}</span>
              </span>
            ) : (
              <span className="text-gray-400">Runbook not identified</span>
            )}
            {nid && (
              <span className="font-mono text-[10.5px] text-gray-400 bg-gray-50 border border-gray-100 rounded px-1.5 py-[1px]">
                {nid}
              </span>
            )}
          </span>

          {preview && !expanded && (
            <span className="mt-1.5 block text-[12.5px] text-gray-600 italic truncate">
              &ldquo;{preview}&rdquo;
            </span>
          )}

          {showBar && (
            <span className="mt-2 block h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <span
                className="block h-full rounded-full bg-cyan-500"
                style={{ width: `${Math.max(6, (count / maxCount) * 100)}%` }}
              />
            </span>
          )}
        </span>

        <span className="flex-shrink-0 text-right">
          <span className="inline-flex items-center justify-center min-w-[28px] rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 tabular-nums">
            {count}
          </span>
          <span className="block text-[10.5px] text-gray-400 mt-1">
            {count === 1 ? "time overridden" : "times overridden"}
          </span>
        </span>
      </button>

      {/* ── expanded: the whole story of this fork ────────────────────── */}
      {expanded && (
        <div className="px-5 pb-5 pl-12">
          {asked && (
            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
                What the engine proposed
              </p>
              <p className="text-[13px] text-[#253439] bg-[#FAFAF6] border border-[#EBECE6] rounded-lg px-3 py-2">
                {asked}
              </p>
            </div>
          )}

          {(branches.length > 0 || destinations.length > 0 || anyUnknown) && (
            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">
                Where the path split
              </p>
              <div className="border border-[#EBECE6] rounded-lg bg-white px-2 py-2 overflow-x-auto">
                <OverrideFork
                  node={{ nid, title, node_type: type }}
                  suggested={branches}
                  destinations={destinations}
                  unknownDestination={destinations.length === 0 && anyUnknown}
                />
              </div>
            </div>
          )}

          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">
              What the engineer said instead
              {examples.length > 1 ? ` (${examples.length})` : ""}
            </p>
            {examples.length === 0 ? (
              <p className="text-[12.5px] text-gray-400">
                No engineer wording was captured for this step.
              </p>
            ) : (
              <ul className="list-none pl-0 space-y-3">
                {examples.map((ex, i) => (
                  <li
                    key={`${ex.session_pk || "?"}-${i}`}
                    className="border-l-2 border-amber-300 pl-3"
                  >
                    <p className="text-[13px] text-[#253439] flex gap-1.5">
                      <Quote size={12} className="mt-1 flex-shrink-0 text-amber-400" />
                      <span>{asText(ex.query_text)}</span>
                    </p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-gray-400">
                      {shortDate(ex.at) && <span>{shortDate(ex.at)}</span>}
                      <StatusChip status={ex.status} />
                      {asText(ex.symptom_text) && (
                        <span className="truncate max-w-[420px]">
                          on: {asText(ex.symptom_text)}
                        </span>
                      )}
                      {ex.destination?.nid ? (
                        <span>
                          went to:{" "}
                          <span className="text-gray-600">
                            {asText(ex.destination.title) || ex.destination.nid}
                          </span>
                          {ex.destination.origin === "derived" && (
                            <span className="text-gray-400">
                              {" "}
                              (next step in that trail)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">
                          trail ends here
                        </span>
                      )}
                      {ex.session_pk != null && (
                        <button
                          type="button"
                          onClick={() => onOpenTrail(ex.session_pk)}
                          className="inline-flex items-center gap-1 text-cyan-600 hover:underline font-medium"
                        >
                          View trail
                          <ExternalLink size={11} />
                        </button>
                      )}
                    </div>
                    {asText(ex.proposed_text) && asText(ex.proposed_text) !== asked && (
                      <p className="mt-1 text-[11.5px] text-gray-400 italic">
                        Engine proposed: &ldquo;{asText(ex.proposed_text)}&rdquo;
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RunbookGaps;
